import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  DestroyRef,
  EnvironmentProviders,
  Injectable,
  PLATFORM_ID,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { environment } from '@env';
import { filter } from 'rxjs';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export const provideGoogleAnalytics = (): EnvironmentProviders =>
  provideAppInitializer(() => {
    inject(GoogleAnalyticsService).init();
  });

@Injectable({ providedIn: 'root' })
export class GoogleAnalyticsService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly measurementId = environment.GA_MEASUREMENT_ID;

  init(): void {
    if (!this.isBrowser || !this.measurementId) return;

    window.dataLayer = window.dataLayer || [];
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer!.push(arguments);
      };
    window.gtag('js', new Date());
    window.gtag('config', this.measurementId, { send_page_view: false });
    const s = this.document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`;
    this.document.head.appendChild(s);

    this.trackPageViews();
  }

  trackEvent(name: string, params: Record<string, unknown> = {}): void {
    this.safe('trackEvent', () => {
      window.gtag!('event', name, params);
    });
  }

  private isReady(): boolean {
    return this.isBrowser && typeof window.gtag === 'function';
  }

  /**
   * Run a gtag invocation only when the tag is ready, containing any throw so
   * analytics failures never surface to the caller.
   */
  private safe(label: string, fn: () => void): void {
    if (!this.isReady()) return;
    try {
      fn();
    } catch (error) {
      console.warn(`[GoogleAnalytics] ${label} failed`, error);
    }
  }

  private trackPageViews(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => {
        this.safe('trackPageViews', () => {
          window.gtag!('event', 'page_view', {
            page_path: event.urlAfterRedirects,
            page_location: window.location.href,
            page_title: this.document.title,
          });
        });
      });
  }
}
